import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import { getKaajSectionSpacing } from "../../../../modules/layout/kaaj-section-spacing";
import type {
  ContactMethodsBlockMethod,
  BlockAction,
  BlockLink,
  ContactMethodsBlockMethodType,
  IContactMethodsBlockProps,
} from "./ContactMethodsBlock.props";
import { RichTextHtml } from "../../RichTextHtml";

const KaajLink = resolveKaajPrimitive("Link");

const accentByType: Record<
  ContactMethodsBlockMethodType,
  {
    dotClass: string;
    iconClass: string;
    railClass: string;
    softGlowClass: string;
  }
> = {
  email: {
    dotClass: "bg-[#8B9CFF]",
    iconClass: "border-[#D6DEFF] bg-[#F4F3FF] text-[#4F46E5]",
    railClass: "from-[#8B9CFF] via-[#D9E1FF] to-transparent",
    softGlowClass: "bg-[#D9E1FF]",
  },
  phone: {
    dotClass: "bg-[#14B8A6]",
    iconClass: "border-[#BDF4F4] bg-[#ECFEFF] text-[#087B94]",
    railClass: "from-[#55D6C2] via-[#BDF4F4] to-transparent",
    softGlowClass: "bg-[#55D6C2]",
  },
  support: {
    dotClass: "bg-[#2144FF]",
    iconClass: "border-[#CAD7FF] bg-[#EEF4FF] text-[#2144FF]",
    railClass: "from-[#2144FF] via-[#AFC0FF] to-transparent",
    softGlowClass: "bg-[#2144FF]",
  },
  sales: {
    dotClass: "bg-[#8B5CF6]",
    iconClass: "border-[#DDD6FE] bg-[#F6F1FF] text-[#6D28D9]",
    railClass: "from-[#8B5CF6] via-[#DDD6FE] to-transparent",
    softGlowClass: "bg-[#8B5CF6]",
  },
  general: {
    dotClass: "bg-[#EC4899]",
    iconClass: "border-[#FBCFE8] bg-[#FFEAF9] text-[#BE185D]",
    railClass: "from-[#EC4899] via-[#FBCFE8] to-transparent",
    softGlowClass: "bg-[#EC4899]",
  },
};

const isBlankTarget = (target?: string): boolean => target === "_blank";
const isExternalHref = (href?: string | null): boolean => /^https?:\/\//i.test(href ?? "");

const getRel = (action: BlockLink): string | undefined =>
  isBlankTarget(action.target ?? undefined) || isExternalHref(action.href)
    ? "noopener noreferrer"
    : undefined;

const getActionKey = (action: BlockAction, index: number): string =>
  `${action.text}-${action.href}-${index}`;

const getButtonClassName = (style?: string, tone: "default" | "inverse" = "default") => {
  const base =
    "inline-flex min-h-11 items-center justify-center rounded-full px-5 py-2.5 text-center text-sm font-extrabold transition-[background-color,border-color,box-shadow,color,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-4 motion-reduce:transition-none motion-safe:hover:-translate-y-0.5 max-[420px]:w-full";

  if (style === "link") {
    return cn(
      base,
      "px-1 shadow-none motion-safe:hover:translate-x-0.5 motion-safe:hover:-translate-y-0",
      tone === "inverse"
        ? "text-white underline decoration-white/30 underline-offset-4 hover:text-[#F6F1FF] hover:decoration-white focus-visible:ring-white focus-visible:ring-offset-[#2144FF]"
        : "text-[#2144FF] underline decoration-[#2144FF]/25 underline-offset-4 hover:text-[#132EDB] hover:decoration-[#132EDB] focus-visible:ring-[#2144FF] focus-visible:ring-offset-white",
    );
  }

  if (style === "ghost") {
    return cn(
      base,
      tone === "inverse"
        ? "border border-white/24 bg-white/10 text-white shadow-[0_14px_34px_rgba(21,25,39,0.12)] hover:border-white/54 hover:bg-white/16 focus-visible:ring-white focus-visible:ring-offset-[#2144FF]"
        : "border border-[#D9E1FF] bg-[#F6F1FF] text-[#2144FF] shadow-[0_10px_24px_rgba(33,68,255,0.08)] hover:border-[#BFD0FF] hover:bg-[#EDE7FF] hover:text-[#132EDB] focus-visible:ring-[#2144FF] focus-visible:ring-offset-white",
    );
  }

  if (tone === "inverse") {
    if (style === "outline" || style === "secondary") {
      return cn(
        base,
        "border border-white/34 bg-white/10 text-white shadow-[0_14px_34px_rgba(21,25,39,0.12)] hover:border-white/62 hover:bg-white/16 focus-visible:ring-white focus-visible:ring-offset-[#2144FF]",
      );
    }

    return cn(
      base,
      "border border-white bg-white text-[#2144FF] shadow-[0_18px_42px_rgba(21,25,39,0.18)] hover:bg-[#F6F1FF] focus-visible:ring-white focus-visible:ring-offset-[#2144FF]",
    );
  }

  if (style === "outline") {
    return cn(
      base,
      "border border-[#BFD0FF] bg-white text-[#2144FF] shadow-[0_12px_30px_rgba(33,68,255,0.08)] hover:border-[#2144FF] hover:bg-[#F6F1FF] hover:text-[#132EDB] focus-visible:ring-[#2144FF] focus-visible:ring-offset-white",
    );
  }

  if (style === "secondary") {
    return cn(
      base,
      "border border-[#D9E1FF] bg-[#F6F1FF] text-[#2144FF] hover:border-[#BFD0FF] hover:bg-[#EDE7FF] hover:text-[#132EDB] focus-visible:ring-[#2144FF] focus-visible:ring-offset-white",
    );
  }

  return cn(
    base,
    "border border-[#2144FF] bg-[#2144FF] text-white shadow-[0_14px_32px_rgba(33,68,255,0.24)] hover:border-[#132EDB] hover:bg-[#132EDB] focus-visible:ring-[#2144FF] focus-visible:ring-offset-white",
  );
};

const ActionLink = ({
  action,
  className,
  tone = "default",
}: {
  action: BlockAction;
  className?: string;
  tone?: "default" | "inverse";
}) => {
  return (
    <KaajLink
      aria-label={action.ariaLabel || action.text}
      className={cn(getButtonClassName(action.style, tone), className)}
      href={action.href}
      rel={getRel(action)}
      target={action.target}
    >
      <span>{action.text}</span>
      {action.style === "link" ? null : (
        <span aria-hidden="true" className="ml-2">
          →
        </span>
      )}
    </KaajLink>
  );
};

const ContactIcon = ({ type }: { type: ContactMethodsBlockMethodType }) => {
  const commonProps = {
    "aria-hidden": true,
    className: "size-6",
    fill: "none",
    stroke: "currentColor",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    strokeWidth: 1.8,
    viewBox: "0 0 24 24",
  };

  switch (type) {
    case "email":
      return (
        <svg {...commonProps}>
          <path d="M4.5 6.75h15v10.5h-15z" />
          <path d="m5 7 7 6 7-6" />
        </svg>
      );
    case "phone":
      return (
        <svg {...commonProps}>
          <path d="M6.5 4.75 9 4l2 4-1.5 1.25a9.5 9.5 0 0 0 5.25 5.25L16 13l4 2-.75 2.5c-.25.84-1.08 1.35-1.95 1.2A15.5 15.5 0 0 1 5.3 6.7c-.15-.87.36-1.7 1.2-1.95Z" />
        </svg>
      );
    case "support":
      return (
        <svg {...commonProps}>
          <path d="M5 12a7 7 0 1 1 14 0" />
          <path d="M5 12v3a2 2 0 0 0 2 2h1v-5H5Zm14 0v3a2 2 0 0 1-2 2h-1v-5h3Z" />
          <path d="M9 19h3.5a3.5 3.5 0 0 0 3.5-3.5" />
        </svg>
      );
    case "sales":
      return (
        <svg {...commonProps}>
          <path d="M7 7.5h10v13H7z" />
          <path d="M9 7.5A3 3 0 0 1 12 4a3 3 0 0 1 3 3.5" />
          <path d="M9.5 12h5M9.5 15h4" />
        </svg>
      );
    default:
      return (
        <svg {...commonProps}>
          <path d="M6.5 5.5h11v9h-7L6.5 18v-3.5h0z" />
          <path d="M9 9h6M9 12h4" />
        </svg>
      );
  }
};

const MethodValue = ({
  featured,
  method,
}: {
  featured?: boolean;
  method: ContactMethodsBlockMethod;
}) => {
  if (method.link?.href) {
    return (
      <KaajLink
        aria-label={method.link.ariaLabel || method.value}
        className={cn(
          "inline-flex min-h-11 max-w-full items-center rounded-xl text-left font-extrabold leading-tight tracking-[-0.035em] underline-offset-4 transition-[color,decoration-color] duration-200 [overflow-wrap:anywhere] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-4 motion-reduce:transition-none",
          featured
            ? "mt-4 text-[1.75rem] lg:text-[2.4rem] text-white decoration-white/24 hover:decoration-white focus-visible:ring-white focus-visible:ring-offset-[#2144FF]"
            : "mt-2 text-[1.25rem] lg:text-[1.6rem] text-[#151927] decoration-[#2144FF]/20 hover:text-[#2144FF] hover:decoration-[#2144FF] focus-visible:ring-[#2144FF] focus-visible:ring-offset-white",
        )}
        href={method.link.href}
        rel={getRel(method.link)}
        target={method.link.target ?? undefined}
      >
        {method.value}
      </KaajLink>
    );
  }

  return (
    <p
      className={cn(
        "max-w-full font-extrabold leading-tight tracking-[-0.035em] [overflow-wrap:anywhere]",
        featured
          ? "mt-4 text-[1.75rem] lg:text-[2.4rem] text-white"
          : "mt-2 text-[1.25rem] lg:text-[1.6rem] text-[#151927]",
      )}
    >
      {method.value}
    </p>
  );
};

const FeaturedContactMethodsBlockMethod = ({
  compact = false,
  method,
}: {
  compact?: boolean;
  method: ContactMethodsBlockMethod;
}) => {
  const typeKey = method.type;
  const accent = accentByType[typeKey];

  return (
    <article
      className="relative isolate min-w-0 overflow-hidden rounded-[1.85rem] border border-[#AFC0FF]/60 bg-[radial-gradient(circle_at_88%_16%,rgba(255,255,255,0.24),transparent_28%),linear-gradient(135deg,#2144FF_0%,#176AFF_62%,#6F5BFF_100%)] p-6 text-white shadow-[0_28px_82px_rgba(33,68,255,0.22)] sm:p-7 lg:p-8"
      data-contact-method-type={typeKey}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent"
      />
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute -right-14 top-16 -z-10 size-44 rounded-full opacity-30 blur-3xl",
          accent.softGlowClass,
        )}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-20 -left-16 -z-10 size-52 rounded-full bg-[#FFEAF9]/20 blur-3xl"
      />

      <div
        className={cn("grid gap-8", !compact && "lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end")}
      >
        <div className="min-w-0">
          <div className="flex items-start justify-between gap-4">
            <span
              aria-hidden="true"
              className="flex size-14 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/12 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.24)]"
            >
              <ContactIcon type={typeKey} />
            </span>
            <span className="inline-flex max-w-[12rem] items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-right text-xs font-extrabold uppercase tracking-[0.16em] text-[#E8EEFF] [overflow-wrap:anywhere]">
              <span aria-hidden="true" className="size-2 rounded-full bg-[#55D6C2]" />
              {method.label}
            </span>
          </div>

          <div className="mt-8 sm:mt-10">
            <MethodValue featured method={method} />
            {method.description ? (
              <p className="mt-5 max-w-[52ch] text-pretty text-base leading-7 text-[#E8EEFF] [overflow-wrap:anywhere]">
                {method.description}
              </p>
            ) : null}
          </div>
        </div>

        {method.action ? (
          <div className={cn("flex flex-wrap gap-3", !compact && "lg:justify-end")}>
            <ActionLink action={method.action} tone="inverse" />
          </div>
        ) : null}
      </div>
    </article>
  );
};

const SecondaryContactMethodsBlockMethod = ({ method }: { method: ContactMethodsBlockMethod }) => {
  const typeKey = method.type;
  const accent = accentByType[typeKey];

  return (
    <article
      className="group/route relative isolate min-w-0 overflow-hidden rounded-[1.45rem] border border-[#D9E1FF] bg-white p-4 text-[#151927] shadow-[0_18px_52px_rgba(33,68,255,0.07)] transition-[border-color,box-shadow,transform] duration-300 motion-reduce:transition-none motion-safe:hover:-translate-y-0.5 motion-safe:hover:border-[#BFD0FF] motion-safe:hover:shadow-[0_26px_68px_rgba(33,68,255,0.12)] sm:p-5"
      data-contact-method-type={typeKey}
    >
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute left-5 top-0 h-1.5 w-16 rounded-b-full bg-gradient-to-r",
          accent.railClass,
        )}
      />
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute -right-10 -top-10 -z-10 size-28 rounded-full opacity-14 blur-2xl transition-transform duration-300 motion-reduce:transition-none motion-safe:group-hover/route:scale-110",
          accent.softGlowClass,
        )}
      />

      <div className="grid gap-4 sm:grid-cols-[3.5rem_minmax(0,1fr)]">
        <span
          aria-hidden="true"
          className={cn(
            "flex size-14 shrink-0 items-center justify-center rounded-2xl border shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]",
            accent.iconClass,
          )}
        >
          <ContactIcon type={typeKey} />
        </span>

        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-[0.72rem] font-extrabold uppercase tracking-[0.16em] text-[#405076] [overflow-wrap:anywhere]">
            <span aria-hidden="true" className={cn("size-2 rounded-full", accent.dotClass)} />
            {method.label}
          </h3>
          <MethodValue method={method} />
          {method.description ? (
            <p className="mt-3 max-w-[52ch] text-pretty text-sm leading-6 text-[#4B5568] [overflow-wrap:anywhere]">
              {method.description}
            </p>
          ) : null}
          {method.action ? (
            <div className="mt-4">
              <ActionLink action={method.action} className="px-4 py-2" />
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
};

export const ContactMethodsBlock = (props: IContactMethodsBlockProps) => {
  const { anchorId, parentClass, spacing } = props;
  const eyebrow = props.eyebrow;
  const heading = props.heading;
  const subheading = props.subheading;
  const contactMethods = props.contactMethods ?? [];
  const blockActions = props.buttons ?? [];
  const hasSingleMethod = contactMethods.length === 1;
  const shouldFeatureFirstMethod = hasSingleMethod || contactMethods.length >= 3;
  const featuredMethod = shouldFeatureFirstMethod ? contactMethods[0] : undefined;
  const secondaryMethods = shouldFeatureFirstMethod ? contactMethods.slice(1) : contactMethods;
  const hasHeader = Boolean(eyebrow || heading || subheading || blockActions.length);
  const headingId = heading && anchorId ? `${anchorId}-heading` : undefined;

  if (!hasHeader && contactMethods.length === 0) {
    return null;
  }

  return (
    <section
      className={cn(
        "relative isolate overflow-hidden text-[#151927]",
        getKaajSectionSpacing(spacing),
        parentClass,
      )}
      aria-labelledby={headingId}
      data-block-handle="contactMethods"
      id={anchorId ?? undefined}
    >
      <div className="kaaj-container relative">
        {hasHeader ? (
          <div className="grid gap-6">
            <div className="space-y-4">
              {eyebrow ? (
                <p className="inline-flex max-w-full items-center gap-2 rounded-full border border-[#CAD7FF] bg-white/92 px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.18em] text-[#2144FF] shadow-[0_10px_26px_rgba(33,68,255,0.1)] [overflow-wrap:anywhere]">
                  <span aria-hidden="true" className="size-2 rounded-full bg-[#55D6C2]" />
                  {eyebrow}
                </p>
              ) : null}
              {heading ? (
                <h2 id={headingId} className="kaaj-section-heading text-[#151927]">
                  {heading}
                </h2>
              ) : null}
              {subheading ? (
                <RichTextHtml
                  className="max-w-[64ch] text-pretty text-base leading-7 text-[#4B5568] [overflow-wrap:anywhere] sm:text-lg sm:leading-8"
                  html={subheading}
                />
              ) : null}
            </div>

            {blockActions.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {blockActions.map((action, index) => (
                  <ActionLink action={action} key={getActionKey(action, index)} />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {contactMethods.length > 0 ? (
          <div className={cn("mt-10 lg:mt-8", !hasHeader && "mt-0")}>
            <div
              className={cn(
                "relative overflow-hidden rounded-[2.25rem] border border-[#D9E1FF] bg-white/92 p-3 shadow-[0_28px_88px_rgba(33,68,255,0.12)] sm:p-4 lg:p-5",
                hasSingleMethod && "mx-auto max-w-4xl",
              )}
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-[#B8C7FF] to-transparent"
              />
              <div className="grid gap-3 lg:gap-4">
                {featuredMethod ? (
                  <FeaturedContactMethodsBlockMethod
                    compact={hasSingleMethod}
                    method={featuredMethod}
                  />
                ) : null}
                {secondaryMethods.length > 0 ? (
                  <div className="grid gap-3 lg:grid-cols-2">
                    {secondaryMethods.map((method, index) => (
                      <SecondaryContactMethodsBlockMethod
                        key={`${method.type}-${method.label}-${index}`}
                        method={method}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

ContactMethodsBlock.displayName = "Block::ContactMethodsBlock";
